import { Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { lazyWithReload as lazy } from '../../utils/staleBuild';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { consumeCrashReport, crashReportBelongsHere, type FeedbackPrefill } from '../../utils/feedbackPrefill';
import { useIsPsiStaff } from '../../utils/psiStaff';

const ReportProblemDialog = lazy(() => import('./ReportProblemDialog'));

const OPEN_EVENT = 'dietech:report-problem';

/** Whether this user gets Report a problem at all (the button's own gate). */
export function useReportProblemAvailable() {
  const psi = useIsPsiStaff();
  return import.meta.env.VITE_FEEDBACK_ENABLED === 'true' && psi;
}

/** Opens the dialog from elsewhere: the phone More sheet, where the bar has no button. */
export function openReportProblem() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/**
 * "Report a problem" trigger for the top toolbar.
 *
 * Lives beside the notification bell rather than as a floating action button: testers reach for it
 * where every other global action already is, and a FAB pinned over the bottom-right corner
 * collides with grid scrollbars and the mobile tab bar on exactly the screens they use most.
 *
 * The dialog and html2canvas-pro stay lazy — the button is on every screen, but firing it is rare.
 *
 * Gated on VITE_FEEDBACK_ENABLED. That check is a compile-time constant, so with the flag off
 * Rollup drops this component and the dialog from the bundle entirely. With it on, only a PSI
 * login sees the button.
 *
 * `trigger={false}` keeps the dialog (and the crash pickup below) without the button: on a phone the
 * More sheet opens it with `openReportProblem`.
 */
export default function ReportProblemToolbarButton({ trigger = true }: { trigger?: boolean }) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<FeedbackPrefill | null>(null);

  const enabled = useReportProblemAvailable();
  const { pathname } = useLocation();

  // A crash parks a prefilled report in sessionStorage before its tree unmounts; pick it up once
  // the app is alive again and open the dialog with the error already described. Only on the page
  // that crashed or the dashboard (crashReportBelongsHere); on any other page it waits.
  useEffect(() => {
    if (!enabled || !crashReportBelongsHere(pathname)) return;
    const parked = consumeCrashReport();
    if (parked) {
      setPrefill(parked);
      setOpen(true);
    }
  }, [enabled, pathname]);

  useEffect(() => {
    if (!enabled) return undefined;
    const openBlank = () => {
      setPrefill(null);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, openBlank);
    return () => window.removeEventListener(OPEN_EVENT, openBlank);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      {trigger && (
        <Tooltip title="Report a problem with this screen" arrow>
          <IconButton
            size="small"
            onClick={() => {
              setPrefill(null);
              setOpen(true);
            }}
            aria-label="Report a problem with this screen"
            data-feedback-ignore="true"
            sx={{ color: '#BA4A20' }}
          >
            <i className="bi bi-megaphone-fill" aria-hidden="true" style={{ fontSize: '1.05rem' }} />
          </IconButton>
        </Tooltip>
      )}

      {open && (
        <Suspense fallback={null}>
          <ReportProblemDialog
            open={open}
            prefill={prefill}
            onClose={() => {
              setOpen(false);
              setPrefill(null);
            }}
          />
        </Suspense>
      )}
    </>
  );
}
